import rison from "rison";
import type { DriveStore } from "../store/reducers";
export interface IRouterPageState {
    loggingIn?: boolean;
    credentials?: ICredentials;
    next?: string;
    cleanUp?: boolean;
    server?: string;
    embed?: boolean;
    iam?: boolean;
    addinName?: string;
}

export function getCurrentHostName(includeProtocol = false): string {
    let loc = window.location;
    return `${ includeProtocol ? `${ loc.protocol }//` : "" }${ loc.host }`;
}

export function getHomeHash(init = true): string {
    return "main" + (init ? ("," + rison.encode({ init: true })) : "");
}

/**
 * Hackers may encode url multiple times this function will decode url
 * @param uri The URI to decode
 */
export function decodeUri (uri: string): string {
    let prevUri = uri;
    let decodedUri = decodeURIComponent(uri);

    while (decodedUri !== prevUri) {
        prevUri = decodedUri;
        decodedUri = decodeURIComponent(decodedUri);
    }

    return decodedUri;
}

export function getDatabaseNameFromPath(uri?: string): string | undefined {
    let splitPath = (uri || decodeUri(window.location.pathname)).split("/");
    let dbNamePath = (splitPath[1] || "").toLowerCase();
    let drivePath = (splitPath[2] || "").toLowerCase();
    // if there is a dbName but drivePath != drive, the url format is unexpected, so return undefined
    return dbNamePath && dbNamePath !== "drive" && drivePath === "drive" ? dbNamePath : undefined;
}

/**
 * Check whether driver is on the login page
 * @param hash If not provided will use location.hash
 */
export function isOnLogin(hash?: string): boolean {
    let thisHash = hash || location.hash;
    return /((?:ui\/)|^)*login/.test(thisHash);
}

/**
 * Check if the driver is doing an auth zero login
 * @param hash If not provided will use location.hash
 */
export function isIamLogin(hash?: string): boolean {
    const thisHash = decodeURIComponent(hash || location.hash);
    const stateRegexMatch = thisHash.match(/^#(.*?)(,(.*))?$/);
    const parsedHash: IRouterPageState | undefined = stateRegexMatch && stateRegexMatch[3] ? rison.decode(stateRegexMatch[3]) : undefined;
    return isOnLogin(thisHash) && !!parsedHash?.iam;
}

/**
 * Check whether on the codriver login page
 * @param hash If not provided will use location.hash
 */
export function isCoDriverLoggingIn(hash?: string): boolean {
    let thisHash = hash || location.hash;
    return /((?:ui\/)|^)*login,\(addCoDriver:true\)/.test(thisHash);
}

/**
 * Check whether on the switch-user login page.
 * @param hash If not provided will use location.hash
 */
export function isOnSwitchUserLogin(hash?: string): boolean {
    let thisHash = hash || location.hash;
    return /((?:ui\/)|^)*login,\(switchUser:/.test(thisHash);
}

/**
 * Checks whether driver is on login screen or workflow.
 * @param hash If not provided it will use document.location.hash
 */
export function isDriverOnWorkflow(hash?: string): boolean {
    let thisHash = hash || location.hash;
    return /((?:ui\/)|^)*login(?!,\(addCoDriver:true\))/.test(thisHash) || /((?:ui)|^)*\/?workflow/.test(thisHash);
}

/**
 * Checks whether driver is on login screen or workflow. Almost identical except it counts the codriver login view as well.
 * @param hash If not provided it will use document.location.hash
 */
export function isDriverOnAnyWorkflow(hash?: string): boolean {
    return isDriverOnWorkflow(hash) || isCoDriverLoggingIn(hash);
}

/**
 * Checks whether driver is on a page that requires 'back' functionality.
 * @param hash If not provided it will use document.location.hash
 */
export function isDriverDoingNestedWorkflow(hash?: string): boolean {
    let thisHash = hash || location.hash;
    return /((?:ui)|^)*\/?step:(doDVIR)|(selectShipment)|(selectTrailer)|(acceptLog)|(addLog)/.test(thisHash);
}

/**
 * Route of the read-only Inspection History Detail page (MYG-141213). The bare segment is
 * what the router's page matching sees, PATH is the shell-prefixed form found in the
 * location hash, and HASH is what Backbone-side navigation must pass. Every call site must
 * derive from these constants so the route can only change in one place.
 */
export const INSPECTION_HISTORY_DETAILS_ROUTE = "inspections/historyDetails";
export const INSPECTION_HISTORY_DETAILS_PATH = `/-/${ INSPECTION_HISTORY_DETAILS_ROUTE }`;
export const INSPECTION_HISTORY_DETAILS_HASH = `#${ INSPECTION_HISTORY_DETAILS_PATH }`;

/**
 * True while any driver has an active roadside inspection session (PIN-locked or no-PIN).
 * Canonical predicate for "is a roadside session active" — inspectionHistoryDetails.tsx uses
 * it to register its ExitInspectionMode handler, so the topbar lockdowns and the exit handler
 * can never drift apart. Falls back to window.driveApp when no store is passed because this
 * module's hash helpers have no store parameter (same pattern as utils/device.ts). Right
 * after a reload window.driveApp can be briefly undefined — treated as "no session".
 */
export function hasActiveRoadsideSession(store?: DriveStore): boolean {
    const pins = (store ?? window.driveApp?.store)?.getState().data?.inspectionPin ?? {};
    return Object.keys(pins).some(key => !!pins[key]);
}

/**
 * Checks whether driver is moving around in inspection mode (want to lock things down similar to the workflow).
 * @param hash If not provided it will use document.location.hash
 */
export function isDriverNavigatingInspectionMode(hash?: string): boolean {
    let thisHash = hash || location.hash;
    // The regex covers legacy Backbone inspection URLs (#inspection/..., #ui/inspection/...),
    // which only exist inside Roadside Check.
    if (/\#((?:ui\/)|^)*\/?inspection/.test(thisHash)) {
        return true;
    }
    // The React Inspection History Detail page participates in inspection mode ONLY while a
    // roadside session is active — it is also reachable from normal Inspection History
    // navigation (MYG-115128). Anchor the match to the start of the hash so login/workflow
    // hashes that merely reference the route in rison state (e.g.
    // ",next:'ui/-/inspections/historyDetails'") do not count as being on the page (MYG-141787).
    if (!thisHash.startsWith(INSPECTION_HISTORY_DETAILS_HASH)) {
        return false;
    }
    // The route must end at a delimiter (or the hash end) so future sibling routes such as
    // "/-/inspections/historyDetailsEdit" are not captured by the prefix match.
    const nextChar = thisHash.charAt(INSPECTION_HISTORY_DETAILS_HASH.length);
    return (nextChar === "" || nextChar === "," || nextChar === "/" || nextChar === "?") && hasActiveRoadsideSession();
}

/**
 * Checks if user is viewing inspection mode. Different from navigation, we may be navigating away but still be viewing inspection mode.
 * @param hash If not provided it will use document.location.hash
 */
export function isDriverViewingInspectionMode(hash?: string): boolean {
    return isDriverNavigatingInspectionMode(hash) || !!document.getElementById("inspection-container");
}

/**
 * Route pattern for the DVIR (asset inspection) area - matches both the DVIR list/start page
 * (ui/dvir/main) and the active inspect form (ui/dvir/inspect). Deliberately broad (an unanchored
 * substring match) because its only consumer, REFRESH_PROTECTED_URLS_REGEX, uses it to delay an
 * automatic service-worker refresh - a false positive there merely delays a refresh.
 *
 * Do NOT reuse this for anything higher-stakes (e.g. gating a duty-status prompt): see
 * DVIR_INSPECT_PAGE_REGEX below for a pattern safe for that purpose.
 */
export const DVIR_PAGE_REGEX = /((?:ui\/)|^)*\/?dvir/;

/**
 * Route pattern for the active DVIR inspection form specifically (ui/dvir/inspect) - excludes the
 * DVIR list page (ui/dvir/main), where no in-flight inspection work exists yet. Anchored (a path
 * separator or string start must immediately precede "dvir", and a path separator, "?", "," or
 * end-of-string must immediately follow "inspect") so a value that merely contains "dvir"
 * elsewhere in the hash - a query parameter, or a different route sharing the substring - cannot
 * false-positive. Used by isDriverViewingDvirMode() below (MYG-151386), which gates duty-status
 * prompt suppression, where a false positive would silently hide a driver-facing HOS prompt.
 */
export const DVIR_INSPECT_PAGE_REGEX = /(^|[#/])(ui\/)?dvir\/inspect(\/|$|[?,])/;

/**
 * True while the driver is on the active DVIR (asset inspection) form. Distinct from
 * isDriverNavigatingInspectionMode() / isDriverViewingInspectionMode(), which cover only the
 * legacy Roadside Check inspection flow - DVIR is a structurally separate feature with its own
 * router branch (ui/dvir/*) and view (InspectDvirView), so it was previously invisible to those
 * checks (MYG-151386 / ESUPSD-56293: the stopped-driving prompt could surface over an open DVIR
 * and its "No" response would write a manual Driving log mid-inspection).
 *
 * Also checks for the "new-inspection-panel" DOM id (mirroring isDriverViewingInspectionMode()'s
 * "inspection-container" fallback) so a route evaluated mid-navigation - between the hash changing
 * and the new view actually mounting - still correctly counts as "in the DVIR form" rather than
 * racily missing it.
 * @param hash If not provided it will use document.location.hash
 */
export function isDriverViewingDvirMode(hash?: string): boolean {
    let thisHash = hash || location.hash;
    return DVIR_INSPECT_PAGE_REGEX.test(thisHash) || !!document.getElementById("new-inspection-panel");
}

/**
 * Regexes for protected pages where automatic service worker update should be delayed.
 */
export const REFRESH_PROTECTED_URLS_REGEX = [
    DVIR_PAGE_REGEX,  // DVIR page
    /((?:ui\/)|^)*\/?assignUnassigned/,  // Unassigned hos logs page
    /((?:ui\/)|^)*\/?selectVehicle/,
    /((?:ui\/)|^)*\/?selectTrailer/,
    /((?:ui\/)|^)*\/?selectShipment/,
    /((?:ui\/)|^)*\/?hos\/tabs\/logs\/view.*[,\(]id:/, // Edit hos log page
    // Beta Inspections pages with in-flight checklist work (MYG-147114). Deliberately excludes
    // read-only pages (main, submitted, historyDetails) — historyDetails is protected only during
    // an active roadside session via isDriverNavigatingInspectionMode (MYG-141787).
    /\/-\/inspections\/(create|recertify|details|review)/
];

/**
 * Checks if user is on a protected page where automatic reload should be delayed.
 * Protected pages include: login, workflow, and inspection pages.
 * @param hash If not provided it will use document.location.hash
 */
export function isOnProtectedPage(hash?: string): boolean {
    let thisHash = hash || location.hash;

    // Check if on Login, Workflow, or Inspection pages
    if (isOnLogin(thisHash) || isDriverOnWorkflow(thisHash) || isDriverViewingInspectionMode(thisHash)) {
        return true;
    }
    // Check if on any other protected pages
    if (REFRESH_PROTECTED_URLS_REGEX.some(regex => regex.test(thisHash))) {
        return true;
    }
    return false;
}

/**
 * Updates a URL with a new query string. Adapted from https://stackoverflow.com/questions/5999118/how-can-i-add-or-update-a-query-string-parameter
 * @param uri The URI to update
 * @param key The key to add/update
 * @param value The value to add/update for the provided key
 */
export function updateQueryStringParameter(uri: string, key: string, value: string): string {
    let re = new RegExp(`([?&])${ key }=.*?(&|#|$)`, "i");
    if (uri.match(re)) {
        return uri.replace(re, `$1${ key }=${ value }$2`);
    }
    let hash = "";
    let newUri = uri;
    if (newUri.indexOf("#") !== -1) {
        hash = newUri.replace(/.*#/, "#");
        newUri = newUri.replace(/#.*/, "");
    }
    return `${ newUri }${ newUri.indexOf("?") !== -1 ? "&" : "?" }${ key }=${ value }${ hash }`;
}

export function isLocalUrl (url: string): boolean {
    let regExp = new RegExp(`^(.*)(localhost|127\\.0\\.0\\.1|192\\.168\\.(.*)|10\\.(.*)).*$`);
    return !!regExp.test(url);
}

const testDomains = ["mytest.geotab.com", "mytest0.geotab.com", "mytest1.geotab.com", "mytest2.geotab.com"];
export function isTestDomain(domain: string): boolean {
    return isLocalUrl(domain) || testDomains.some(d => domain.indexOf(d) >= 0);
}